import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { convertDate } from '../../global';
import './markdown-renderer.css';

interface MarkdownRendererProps {
    content: string;
    isStreaming?: boolean;
}

const incompleteLinkAtEnd = /!?\[[^\]\n]*(\]\(?[^)\s]*)?$/;

const utcTimestamp = /\b(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)\s*(?:UTC|Z)\b/g;

const formatDates = (content: string) =>
    content.replace(utcTimestamp, (match, date, time) => {
        try {
            return convertDate(`${date}T${time}Z`, 'date') || match;
        } catch {
            return match;
        }
    });

const appRoutePrefixes = [
    '/inquiries-detailview/',
    '/workorders-detailview/',
    '/create-inquiry/',
    '/inquiries',
    '/work-orders'
];

const getInternalAppPath = (href?: string) => {
    if (!href) return '';

    try {
        if (href.startsWith('/')) {
            return appRoutePrefixes.some((prefix) => href.startsWith(prefix)) ? href : '';
        }

        const url = new URL(href, window.location.origin);
        const path = `${url.pathname}${url.search}${url.hash}`;
        const isSameOrigin = url.origin === window.location.origin;

        return isSameOrigin && appRoutePrefixes.some((prefix) => path.startsWith(prefix)) ? path : '';
    } catch {
        return '';
    }
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, isStreaming }) => {
    const renderedContent = React.useMemo(() => {
        const formatted = formatDates(content ?? '');
        return isStreaming ? formatted.replace(incompleteLinkAtEnd, '') : formatted;
    }, [content, isStreaming]);

    return (
        <div 
            className="markdown-content" 
            tabIndex={0} 
            role="region" 
            aria-label="Assistance response"
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    a: ({ href, children, ...props }: any) => {
                        const internalPath = getInternalAppPath(href);

                        if (internalPath) {
                            return (
                                <Link to={internalPath} {...props}>
                                    {children}
                                </Link>
                            );
                        }

                        return (
                            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                {children}
                            </a>
                        );
                    }
                }}
            >
                {renderedContent}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer;
